import * as React from 'react';
import { Check, ChevronLeft, ChevronRight, Save, X, AlertTriangle } from 'lucide-react';

import { Button } from '../primitives/button';
import { cn } from '../lib/utils';
import { ErrorState } from './error-state';
import { PageHeader } from './page-header';
import {
  focusFirstInvalidField,
  normalizeWizardValidation,
  type WizardValidateReturn,
} from './wizard-validation';

/**
 * Canonical wizard contract.
 *
 * Every multi-step create/edit flow should compose `WizardShell`. It bundles
 * the 8 required affordances:
 *
 *   1. Step validation (`steps[i].validate()` — runs on Next; blocks advance on error)
 *   2. Save draft (`onSaveDraft` — manual + auto-save every 30s while dirty)
 *   3. Resume draft (`initialDraft` — restores prior state on mount)
 *   4. Cancel with reason (modal-style reason capture before discarding)
 *   5. Review screen (`renderReview` — final step; renders summary of all values)
 *   6. Final submit (`onSubmit` — promise; throws → `submitError` rendered)
 *   7. Backend error recovery (`submitError` rendered as `ErrorState` with retry)
 *   8. Evidence summary (`evidence` slot in review screen — what will be persisted)
 *
 * Coverage of this primitive is tracked by `scripts/audit-wizard-shell-coverage.mjs`
 * → `docs/wizard-shell-coverage.md`.
 */

export interface WizardStep<TState> {
  /** Stable id; used as React key + URL hash. */
  id: string;
  /** Visible label. */
  label: string;
  /** Optional helper text below the step indicator. */
  description?: string;
  /** Step body. */
  render: (ctx: WizardStepContext<TState>) => React.ReactNode;
  /**
   * Sync or async validator. Return null/empty for valid; for invalid return
   * either a flat `string[]` of messages (summary-only, legacy) or a
   * `WizardValidationResult` that also maps messages to the offending control
   * ids (`{ fields: [{ field: 'qty', message: … }] }`). The richer shape lets
   * `WizardShell` mark the specific control aria-invalid + aria-describedby
   * and focus the first invalid field on a failed Next (WCAG 3.3.1).
   */
  validate?: (state: TState) => WizardValidateReturn | Promise<WizardValidateReturn>;
  /** Optional skip predicate — when true, step is hidden from the rail. */
  skip?: (state: TState) => boolean;
}

export interface WizardStepContext<TState> {
  state: TState;
  setState: React.Dispatch<React.SetStateAction<TState>>;
  /** Per-step summary error messages from the most recent validate(). */
  errors: string[];
  /**
   * Per-field issues from the most recent validate() for THIS step, keyed by
   * the control's DOM id. A step that returns field-scoped errors can spread
   * `fieldErrors[id]` into a `<FieldError>` (or wrap the control in a `<Field>`)
   * to get the screen-reader-announced aria-invalid / describedby association;
   * `WizardShell` already focuses the first invalid control. Empty for steps
   * whose validator only returned summary strings.
   */
  fieldErrors: Record<string, string[]>;
}

export interface WizardEvidenceItem {
  id: string;
  label: string;
  value: React.ReactNode;
}

export interface WizardShellProps<TState> {
  title: string;
  subtitle?: React.ReactNode;
  /** Steps in order. The review/submit step is rendered automatically as the final step. */
  steps: WizardStep<TState>[];
  /** Initial state. */
  initialState: TState;
  /** Optional restored draft. Takes precedence over `initialState`. */
  initialDraft?: TState | null;
  /** Final submit handler. */
  onSubmit: (state: TState) => Promise<void>;
  /** Manual save-draft handler. Also called automatically by the 30s auto-save. */
  onSaveDraft?: (state: TState) => Promise<void>;
  /** Cancel handler. Receives the operator-supplied reason. */
  onCancel?: (reason: string) => void;
  /** Optional renderer for the review-step body — defaults to a JSON dump. */
  renderReview?: (state: TState) => React.ReactNode;
  /** Optional evidence summary list rendered at the bottom of the review step. */
  evidence?: (state: TState) => WizardEvidenceItem[];
  /** Disable autosave (default 30s) — set true for high-cost drafts. */
  disableAutoSave?: boolean;
  className?: string;
}

const REVIEW_STEP_ID = '__review';

/** Stable empty map so a step with no field errors keeps the same ctx identity. */
const EMPTY_FIELD_ERRORS: Record<string, string[]> = Object.freeze({});

export function WizardShell<TState>({
  title,
  subtitle,
  steps,
  initialState,
  initialDraft,
  onSubmit,
  onSaveDraft,
  onCancel,
  renderReview,
  evidence,
  disableAutoSave,
  className,
}: WizardShellProps<TState>) {
  const [state, setState] = React.useState<TState>(initialDraft ?? initialState);
  const [activeIdx, setActiveIdx] = React.useState(0);
  const [errors, setErrors] = React.useState<Record<string, string[]>>({});
  // Per-step, per-field issues from the most recent validate(), keyed
  // step.id → control id → messages (drives per-control aria + focus).
  const [fieldErrors, setFieldErrors] = React.useState<
    Record<string, Record<string, string[]>>
  >({});
  // Wraps the step body so a failed Next can focus the first invalid control.
  const stepBodyRef = React.useRef<HTMLDivElement>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [savingDraft, setSavingDraft] = React.useState(false);
  const [lastSavedAt, setLastSavedAt] = React.useState<Date | null>(null);
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState('');
  const dirtyRef = React.useRef(false);

  React.useEffect(() => {
    dirtyRef.current = true;
  }, [state]);

  const visibleSteps = React.useMemo(
    () => steps.filter((s) => !s.skip?.(state)),
    [steps, state]
  );

  const allSteps = React.useMemo(
    () => [
      ...visibleSteps,
      {
        id: REVIEW_STEP_ID,
        label: 'Review & submit',
        render: () => null,
      } as WizardStep<TState>,
    ],
    [visibleSteps]
  );

  const activeStep = allSteps[activeIdx];
  const isReview = activeStep?.id === REVIEW_STEP_ID;
  const isLastDataStep = activeIdx === visibleSteps.length - 1;

  // Auto-save draft loop.
  React.useEffect(() => {
    if (disableAutoSave || !onSaveDraft) return;
    const id = setInterval(() => {
      if (!dirtyRef.current || submitting) return;
      void doSaveDraft();
    }, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveDraft, disableAutoSave, submitting]);

  async function doSaveDraft() {
    if (!onSaveDraft) return;
    setSavingDraft(true);
    try {
      await onSaveDraft(state);
      setLastSavedAt(new Date());
      dirtyRef.current = false;
    } finally {
      setSavingDraft(false);
    }
  }

  async function handleNext() {
    const step = visibleSteps[activeIdx];
    if (step?.validate) {
      const outcome = normalizeWizardValidation(await step.validate(state));
      if (outcome.hasErrors) {
        setErrors((e) => ({ ...e, [step.id]: outcome.messages }));
        setFieldErrors((fe) => ({ ...fe, [step.id]: outcome.fieldMap }));
        // WCAG 3.3.1 / 2.4.3 — move focus to the first offending control so a
        // keyboard / screen-reader user lands on the field to fix, not just an
        // alert summary. No-op when the validator returned summary-only errors.
        // Deferred so the aria-invalid markup has rendered before focus moves.
        requestAnimationFrame(() => {
          focusFirstInvalidField(stepBodyRef.current, outcome);
        });
        return;
      }
      setErrors((e) => ({ ...e, [step.id]: [] }));
      setFieldErrors((fe) => ({ ...fe, [step.id]: {} }));
    }
    setActiveIdx((i) => Math.min(i + 1, allSteps.length - 1));
  }

  function handleBack() {
    setActiveIdx((i) => Math.max(0, i - 1));
  }

  async function handleSubmit() {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await onSubmit(state);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }

  function handleCancelConfirm() {
    onCancel?.(cancelReason.trim() || 'No reason provided');
    setCancelOpen(false);
  }

  const stepCtx: WizardStepContext<TState> = {
    state,
    setState,
    errors: activeStep ? errors[activeStep.id] ?? [] : [],
    fieldErrors: activeStep ? fieldErrors[activeStep.id] ?? EMPTY_FIELD_ERRORS : EMPTY_FIELD_ERRORS,
  };

  return (
    <div className={cn('flex flex-col gap-4 p-4', className)} data-testid="wizard-shell">
      {/* The title goes through PageHeader, like every other destination.
          This used to be a bespoke `<header><h1>` — a second implementation of
          the page-title affordance, differing from PageHeader only in size
          (text-2xl vs text-xl) and in lacking truncation and breadcrumbs.
          Two implementations meant the shell-contract audit could not see a
          wizard's title at all, and a wizard that wanted to satisfy the audit
          added a PageHeader ON TOP of this one: ModuleInstallWizard rendered
          "Install Module Wizard" directly above "Install module" — two titles
          for one page. One implementation removes both problems.
          The draft-save state moves to `meta`, the slot PageHeader offers for
          exactly this kind of status line. */}
      <PageHeader
        title={title}
        subtitle={subtitle}
        meta={
          lastSavedAt || savingDraft ? (
            <span className="text-xs text-muted-foreground">
              {savingDraft ? 'Saving…' : `Draft saved ${lastSavedAt?.toLocaleTimeString()}`}
            </span>
          ) : undefined
        }
      />

      {/* Step rail */}
      <ol className="flex flex-wrap items-center gap-2" aria-label="Wizard steps">
        {allSteps.map((s, i) => {
          const isActive = i === activeIdx;
          const isDone = i < activeIdx;
          const hasErrors = (errors[s.id]?.length ?? 0) > 0;
          return (
            <li
              key={s.id}
              className={cn(
                'flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset',
                isActive && 'bg-brand-primary/10 text-brand-primary ring-brand-primary/40',
                isDone && !hasErrors && 'bg-[hsl(var(--state-running))]/10 text-[hsl(var(--state-running))] ring-[hsl(var(--state-running))]/30',
                hasErrors && 'bg-[hsl(var(--alarm-critical))]/10 text-[hsl(var(--alarm-critical))] ring-[hsl(var(--alarm-critical))]/40',
                !isActive && !isDone && !hasErrors && 'bg-muted text-muted-foreground ring-border'
              )}
              data-testid={`wizard-step-${s.id}`}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-background/80 text-[10px] font-semibold ring-1 ring-inset ring-current">
                {isDone && !hasErrors ? <Check className="h-3 w-3" aria-hidden /> : i + 1}
              </span>
              {s.label}
            </li>
          );
        })}
      </ol>

      {/* Step body */}
      <div ref={stepBodyRef} className="rounded-md border border-border/60 bg-surface p-4">
        {isReview ? (
          <ReviewStepBody
            state={state}
            renderReview={renderReview}
            evidence={evidence}
            submitError={submitError}
            onRetry={handleSubmit}
          />
        ) : (
          <>
            {activeStep ? activeStep.render(stepCtx) : null}
            {stepCtx.errors.length > 0 ? (
              <div
                className="mt-3 flex items-start gap-2 rounded border border-[hsl(var(--alarm-critical))]/40 bg-[hsl(var(--alarm-critical))]/10 p-2 text-sm text-[hsl(var(--alarm-critical))]"
                role="alert"
                data-testid="wizard-step-errors"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4" aria-hidden />
                <ul className="list-disc pl-4">
                  {stepCtx.errors.map((m, j) => (
                    <li key={j}>{m}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2">
        <div>
          {onCancel ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCancelOpen(true)}
              data-testid="wizard-cancel"
            >
              <X className="mr-1 h-4 w-4" aria-hidden /> Cancel
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {onSaveDraft ? (
            <Button
              variant="outline"
              size="sm"
              onClick={doSaveDraft}
              disabled={savingDraft || submitting}
              data-testid="wizard-save-draft"
            >
              <Save className="mr-1 h-4 w-4" aria-hidden /> Save draft
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            disabled={activeIdx === 0 || submitting}
            data-testid="wizard-back"
          >
            <ChevronLeft className="mr-1 h-4 w-4" aria-hidden /> Back
          </Button>
          {isReview ? (
            <Button size="sm" onClick={handleSubmit} disabled={submitting} data-testid="wizard-submit">
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          ) : (
            <Button size="sm" onClick={handleNext} disabled={submitting} data-testid="wizard-next">
              {isLastDataStep ? 'Review' : 'Next'} <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      </div>

      {/* Cancel-with-reason inline modal */}
      {cancelOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-label="Cancel wizard"
          data-testid="wizard-cancel-modal"
        >
          <div className="w-full max-w-md rounded-lg bg-background p-4 shadow-lg">
            <h2 className="text-lg font-semibold">Discard this draft?</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Provide a reason for cancelling. This is captured in the audit envelope.
            </p>
            <textarea
              className="mt-3 h-24 w-full rounded border border-border p-2 text-sm"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason"
              data-testid="wizard-cancel-reason"
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCancelOpen(false)}>
                Keep editing
              </Button>
              <Button variant="destructive" size="sm" onClick={handleCancelConfirm} data-testid="wizard-cancel-confirm">
                Discard
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ReviewStepBody<TState>(props: {
  state: TState;
  renderReview?: (state: TState) => React.ReactNode;
  evidence?: (state: TState) => WizardEvidenceItem[];
  submitError: string | null;
  onRetry: () => void;
}) {
  const items = props.evidence?.(props.state) ?? [];
  return (
    <div className="flex flex-col gap-4" data-testid="wizard-review">
      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Review</h2>
        {props.renderReview ? (
          props.renderReview(props.state)
        ) : (
          <pre className="overflow-auto rounded bg-muted p-3 text-xs">
            {JSON.stringify(props.state, null, 2)}
          </pre>
        )}
      </section>
      {items.length > 0 ? (
        <section data-testid="wizard-evidence-summary">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Evidence to be persisted
          </h2>
          <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {items.map((it) => (
              <div key={it.id} className="rounded border border-border/60 p-2">
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{it.label}</dt>
                <dd className="mt-1 text-sm">{it.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
      {props.submitError ? (
        <ErrorState
          title="Submit failed"
          message={props.submitError}
          onRetry={props.onRetry}
        />
      ) : null}
    </div>
  );
}
